/**
 * Logger Utility - Centralized logging for Stimulus controllers
 * 
 * Usage:
 *   import { Logger } from '../lib/logger'
 *   
 *   connect() {
 *     Logger.info('Controller connected')
 *     Logger.debug('Debug information', { data })
 *     Logger.warn('Warning message', { context })
 *     Logger.error('Error occurred', error, { extraInfo })
 *   }
 */

class Logger {
  static #cachedEnvironment = null

  static get currentEnvironment() {
    if (this.#cachedEnvironment) return this.#cachedEnvironment
    const metaTag = typeof document !== 'undefined' ? document.querySelector('meta[name="app-env"]') : null
    this.#cachedEnvironment = metaTag?.content || 'unknown'
    return this.#cachedEnvironment
  }

  static get isDevelopment() {
    return this.currentEnvironment === 'development' || this.currentEnvironment === 'test'
  }

  // Debug logs - only in development
  static debug(message, data = {}) {
    if (this.isDevelopment) {
      console.debug(`[DEBUG] ${message}`, data)
    }
  }

  // Info logs - only in development
  static info(message, data = {}) {
    if (this.isDevelopment) {
      console.info(`[INFO] ${message}`, data)
    }
  }

  // Warning logs - only in development
  static warn(message, data = {}) {
    if (this.isDevelopment) {
      console.warn(`[WARN] ${message}`, data)
    }
  }

  // Error logs - in all environments
  static error(message, error = null, data = {}) {
    console.error(`[ERROR] ${message}`, error || data)
    
    // Send to error tracking in production (if available)
    if (!this.isDevelopment && typeof window.ErrorTracker === 'function') {
      window.ErrorTracker.track(message, error)
    }
  }

  // Success logs - only in development
  static success(message) {
    if (this.isDevelopment) {
      console.log(`[SUCCESS] ${message}`)
    }
  }

  // Fatal errors - always log
  static fatal(message, error = null) {
    console.error(`[FATAL] ${message}`, error)
  }

  // Log with context
  static log(level, message, data = {}) {
    const levels = { debug: this.debug, info: this.info, warn: this.warn, error: this.error }
    if (levels[level]) {
      levels[level](message, data)
    } else if (this.isDevelopment) {
      console.log(`[UNKNOWN] ${message}`, data)
    }
  }

  // Log method execution (for debugging)
  static trace(methodName, controller = null) {
    if (this.isDevelopment) {
      const controllerName = controller?.constructor?.name || 'Unknown'
      console.trace(`[TRACE] ${controllerName}#${methodName}`)
    }
  }

  // Log performance timing
  static time(label) {
    if (this.isDevelopment) {
      console.time(`[PERF] ${label}`)
    }
  }

  // Log performance timing end
  static timeEnd(label) {
    if (this.isDevelopment) {
      console.timeEnd(`[PERF] ${label}`)
    }
  }

  // Log API requests
  static api(method, url, data = {}) {
    if (this.isDevelopment) {
      console.log(`[API] ${method} ${url}`, data)
    }
  }

  // Log state changes
  static state(controller, newState) {
    if (this.isDevelopment) {
      console.log(`[STATE] ${controller.constructor.name}`, newState)
    }
  }

  // Clear all logs (for testing)
  static clear() {
    console.clear()
  }
}

export default Logger